import {
  ArrowUpRight,
  CircleNotch,
  DownloadSimple,
  ImageBroken,
  X,
} from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import Lightbox from "yet-another-react-lightbox";

import { Button } from "common/components/ui/Button";

import { isBlobUrl } from "../../../utils";

import type { MessageAttachment } from "../../../types";
import "yet-another-react-lightbox/styles.css";

import useMessageAttachment from "../../../datahooks/useMessageAttachment";

interface AttachmentProps {
  attachment: MessageAttachment;
  isMine: boolean;
  accountId: string;
}

export default function Attachment({
  attachment,
  isMine,
  accountId,
}: AttachmentProps) {
  const { name, url, media_type: mediaType, byte_size: byteSize } = attachment;
  const [hasImageError, setHasImageError] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const { attachmentData, isLoadingAttachmentData, attachmentDataError } =
    useMessageAttachment({
      accountId,
      url,
    });

  const bytesToMegabytes = (bytes: number) => (bytes / 1048576).toFixed(2);

  function downloadAttachment(fileUrl: string): void {
    window.open(fileUrl, "_blank");
  }

  const handleImageError = () => {
    setHasImageError(true);
  };

  // Name, mediaType and byteSize won't be provided
  // from the backend if attachment is image
  const isImage = !mediaType && !byteSize && !name;
  const fileUrl = isBlobUrl(url) ? url : attachmentData?.url;

  if (isImage) {
    // If image failed to load, show a fallback
    if (hasImageError || attachmentDataError) {
      return (
        <div
          className={clsx([
            "relative flex w-full items-center justify-center rounded-2xl p-16 mt-2 lg:p-24 bg-white",
          ])}
        >
          <div className="text-center">
            <ImageBroken size={24} className="mx-auto mb-2 text-purple-500" />
            <p className="text-sm">Image could not be loaded</p>
          </div>
        </div>
      );
    }

    if (isLoadingAttachmentData) {
      return (
        <div
          className={clsx([
            "relative flex w-full items-center justify-center rounded-2xl p-16 mt-2 lg:p-24",
            isMine ? "bg-purple-100" : "bg-black-200",
          ])}
        >
          <div className="text-center">
            <CircleNotch
              size={24}
              className="mx-auto mb-2 animate-spin text-purple-500"
            />
            <p className="text-sm">Loading image...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="relative mt-2">
        <button
          type="button"
          className="w-full cursor-pointer rounded-3xl border-0 bg-transparent object-cover p-0"
          aria-label="Open image preview"
          onClick={() => setIsLightboxOpen(true)}
        >
          <img
            className="rounded-3xl object-cover w-full max-h-[50dvh]"
            src={fileUrl}
            height={200}
            width={200}
            alt=""
            onError={handleImageError}
          />
        </button>

        <Button
          variant="outline-black"
          intent="iconOnly"
          size="sm"
          className="absolute right-2 top-2"
          onClick={(e) => {
            e.preventDefault();
            if (fileUrl) {
              downloadAttachment(fileUrl);
            }
          }}
        >
          <ArrowUpRight size={16} className="text-purple-500" />
        </Button>

        <Lightbox
          open={isLightboxOpen}
          close={() => setIsLightboxOpen(false)}
          slides={[{ src: fileUrl, alt: name || "Attachment" }]}
          carousel={{ finite: true, preload: 0 }}
          styles={{
            container: {
              backgroundColor: "rgb(3 7 18 / 0.9)",
            },
          }}
          render={{
            buttonPrev: () => null,
            buttonNext: () => null,
            iconClose: () => <X size={24} />,
          }}
          controller={{
            closeOnBackdropClick: true,
          }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="flex cursor-pointer flex-row items-center gap-1.5 rounded-3xl bg-white p-1.5 pr-11 transition-colors ease-in-out lg:mt-2"
      aria-label={`Download ${name}`}
      onClick={() => downloadAttachment(fileUrl)}
    >
      <div className="rounded-2xl bg-purple-50 p-2 transition-colors ease-in-out">
        <DownloadSimple size={16} className="text-purple-500" />
      </div>

      <div className="flex w-fit flex-col text-start">
        <span className="line-clamp-1 max-w-[25ch] text-xs-bold">{name}</span>

        <span className="text-button-xxs text-black-500">
          {bytesToMegabytes(byteSize)} MB
        </span>
      </div>
    </button>
  );
}
